/**
 * Hooks for accessing valuation state from the central store
 * Created: 2025-04-09
 * Updated: 2025-04-15 - Updated to use refactored store
 * Updated: 2025-11-08 - Added Turnstile token state for bot protection
 * Updated: 2025-11-09 - Added failure counter and bypass mode for graceful degradation
 */

import { useStore } from '@/store';
import { toast } from 'sonner';
import { useCallback } from 'react';
import { ValuationFormData } from '@/types/validation';
import { getValuation, cleanupValuationData } from '@/components/hero/valuation/services/valuationService';

// A specialized hook for valuation operations
export const useValuationStore = () => {
  // Extract valuation-related parts from the store
  const vin = useStore(state => state.vin);
  const mileage = useStore(state => state.mileage);
  const gearbox = useStore(state => state.gearbox);
  const turnstileToken = useStore(state => state.turnstileToken);
  const turnstileFailureCount = useStore(state => state.turnstileFailureCount);
  const allowBypass = useStore(state => state.allowBypass);
  const isLoading = useStore(state => state.isLoading);
  const dialogOpen = useStore(state => state.dialogOpen);
  const showManualForm = useStore(state => state.showManualForm);
  const valuationResult = useStore(state => state.valuationResult);
  const retryCount = useStore(state => state.retryCount);
  const error = useStore(state => state.error);
  
  // Actions
  const setValuationField = useStore(state => state.setValuationField);
  const setValuationResult = useStore(state => state.setValuationResult);
  const resetValuation = useStore(state => state.resetValuation);
  const captureError = useStore(state => state.captureError);

  // Helper setters
  const setVin = useCallback((value: string) => {
    setValuationField('vin', value);
  }, [setValuationField]);

  const setMileage = useCallback((value: string) => {
    setValuationField('mileage', value);
  }, [setValuationField]);

  const setGearbox = useCallback((value: any) => {
    setValuationField('gearbox', value);
  }, [setValuationField]);

  const setIsLoading = useCallback((value: boolean) => {
    setValuationField('isLoading', value);
  }, [setValuationField]);

  const setDialogOpen = useCallback((value: boolean) => {
    setValuationField('dialogOpen', value);
  }, [setValuationField]);

  const setShowManualForm = useCallback((value: boolean) => {
    setValuationField('showManualForm', value);
  }, [setValuationField]);

  const setTurnstileToken = useCallback((value: string | null) => {
    console.log('[Store] Setting Turnstile token:', value ? 'token set' : 'token cleared');
    setValuationField('turnstileToken', value);
  }, [setValuationField]);

  const clearTurnstileToken = useCallback(() => {
    console.log('[Store] Explicitly clearing Turnstile token');
    setValuationField('turnstileToken', null);
  }, [setValuationField]);

  const setError = useCallback((error: Error | string | null) => {
    setValuationField('error', error);
  }, [setValuationField]);

  const incrementTurnstileFailures = useCallback(() => {
    setValuationField('turnstileFailureCount', turnstileFailureCount + 1);
  }, [turnstileFailureCount, setValuationField]);

  const setAllowBypass = useCallback((value: boolean) => {
    setValuationField('allowBypass', value);
  }, [setValuationField]);

  const incrementRetryCount = useCallback(() => {
    setValuationField('retryCount', retryCount + 1);
    
    // After multiple retries, suggest manual valuation
    if (retryCount + 1 >= 3) {
      toast.info("Having trouble with automatic valuation?", {
        description: "You might want to try manual valuation instead.",
        duration: 5000
      });
    }
  }, [retryCount, setValuationField]);

  const resetRetryCount = useCallback(() => {
    setValuationField('retryCount', 0);
  }, [setValuationField]);

  const resetForm = useCallback(() => {
    setVin('');
    setMileage('');
    setGearbox('manual');
  }, [setVin, setMileage, setGearbox]);

  const resetState = useCallback(() => {
    resetValuation();
    cleanupValuationData();
  }, [resetValuation]);

  // Business logic - Valuation submission
  const handleVinSubmit = async (e: React.FormEvent, formData: ValuationFormData) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await getValuation(
        formData.vin,
        Number(formData.mileage),
        formData.gearbox,
        turnstileToken
      );
      
      if (result.data && result.data.isExisting) {
        toast.error("This vehicle has already been listed");
        return;
      }

      setValuationResult(result.data);
      setDialogOpen(true);
      
      // Clear Turnstile token after successful submission
      setTurnstileToken(null);
      
      // Store valuation data in localStorage (still useful for compatibility)
      localStorage.setItem('valuationData', JSON.stringify(result.data));
      localStorage.setItem('tempVIN', formData.vin);
      localStorage.setItem('tempMileage', formData.mileage);
      localStorage.setItem('tempGearbox', formData.gearbox);
    } catch (error: any) {
      // Only log detailed errors in development
      if (process.env.NODE_ENV !== 'production') {
        console.error('Valuation error:', error);
      }
      setError("Nie udało się pobrać wyceny pojazdu");
      captureError(error);
      // Show user-friendly error message, not raw error
      toast.error("Nie udało się pobrać wyceny pojazdu. Spróbuj ponownie.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // State
    vin,
    mileage,
    gearbox,
    turnstileToken,
    turnstileFailureCount,
    allowBypass,
    isLoading,
    dialogOpen,
    showDialog: dialogOpen, // Alias for backward compatibility
    showManualForm,
    valuationResult,
    error,
    retryCount,
    
    // Setters
    setVin,
    setMileage,
    setGearbox,
    setTurnstileToken,
    clearTurnstileToken,
    incrementTurnstileFailures,
    setAllowBypass,
    setIsLoading,
    setDialogOpen,
    setShowDialog: setDialogOpen, // Alias
    setShowManualForm,
    setValuationResult,
    setError,
    
    // Actions
    handleVinSubmit,
    incrementRetryCount,
    resetRetryCount,
    resetState,
    resetForm
  };
};
